import React, { useState, useEffect, useRef } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actionsCreators from '../../actions/index.js';
import classes from './AiInsert.css';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';

/**
 * AI customer insert — chat with Claude over a fetched WhatsApp group,
 * review the proposed customer cards, and insert each with one click.
 * The insert buttons are programmatic (no AI); the agent only extracts.
 *
 * Layout: actions sidebar (group fetch, recent chats, defaults, Claude login)
 * + a full-height chat column with the composer pinned to the bottom.
 */

// ── Small shared pieces ───────────────────────────────────────────────────────

// Multi-select tag picker: chips + filtered dropdown (TagManager style)
const TagPicker = ({ tags, selected, onChange }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const chosen = selected.map(id => tags.find(t => t.id === id)).filter(Boolean);
  const matches = tags.filter(t =>
    !selected.includes(t.id) &&
    (!query || t.name.toLowerCase().includes(query.toLowerCase()))
  ).slice(0, 10);

  return (
    <div className={classes.tagPicker}>
      <div className={classes.acWrap}>
        <input
          className={classes.input}
          placeholder="הקלידי שם תגית..."
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {open && matches.length > 0 && (
          <div className={classes.acList}>
            {matches.map(t => (
              <div key={t.id} className={classes.acItem}
                onMouseDown={e => { e.preventDefault(); onChange([...selected, t.id]); setQuery(''); }}>
                <span className={classes.tagDot} style={{ background: t.color || '#ccc' }} /> {t.name}
              </div>
            ))}
          </div>
        )}
      </div>
      {chosen.length > 0 && (
        <div className={classes.tagChips}>
          {chosen.map(t => (
            <span key={t.id} className={classes.tagChip} style={{ borderColor: t.color || '#ccc' }}>
              <span className={classes.tagDot} style={{ background: t.color || '#ccc' }} />
              {t.name}
              <button onClick={() => onChange(selected.filter(id => id !== t.id))}>✕</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Result components (entity / table / stats) ───────────────────────────────

const cellText = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const ActionButtons = ({ actions, done, keyPrefix, targetId, onAction }) => (
  <span className={classes.actionBtns}>
    {actions.map((a, i) => {
      const outcome = done[`${keyPrefix}:${i}:${targetId}`];
      if (outcome && outcome.ok) {
        return <span key={i} className={classes.resultOk}>✓ {a.label}</span>;
      }
      return (
        <button key={i}
          className={[classes.btn, classes.btnSm, a.style === 'danger' ? classes.btnDanger : a.style === 'default' ? classes.btnDefault : classes.btnPrimary].join(' ')}
          title={outcome && !outcome.ok ? outcome.error : undefined}
          onClick={() => {
            if (a.confirm && !window.confirm(a.confirm)) return;
            onAction(i, targetId);
          }}>
          {outcome && !outcome.ok ? '⚠ ' : ''}{a.label}
        </button>
      );
    })}
  </span>
);

// Chart color palette
const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c43', '#a05195', '#2f4b7c', '#f95d6a', '#665191'];

// Reusable single chart component (used by both 'chart' and 'charts' kinds)
const SingleChart = ({ chartSpec, height = 300, compact = false }) => {
  const data = (chartSpec.data || []).map(d => ({ name: d.label, value: d.value, color: d.color }));
  const chartType = chartSpec.chartType || 'bar';
  const margins = compact
    ? { top: 10, right: 20, left: 10, bottom: 50 }
    : { top: 20, right: 30, left: 20, bottom: 60 };
  const fontSize = compact ? 10 : 12;

  if (data.length === 0) {
    return <div className={classes.chartEmpty}>אין נתונים</div>;
  }

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <BarChart data={data} margin={margins}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={compact ? 60 : 80} tick={{ fontSize }} />
            <YAxis tick={{ fontSize }} label={chartSpec.yLabel ? { value: chartSpec.yLabel, angle: -90, position: 'insideLeft', fontSize } : undefined} />
            <Tooltip contentStyle={{ direction: 'rtl' }} />
            <Bar dataKey="value" fill="#8884d8" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        );
      case 'line':
        return (
          <LineChart data={data} margin={margins}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={compact ? 60 : 80} tick={{ fontSize }} />
            <YAxis tick={{ fontSize }} label={chartSpec.yLabel ? { value: chartSpec.yLabel, angle: -90, position: 'insideLeft', fontSize } : undefined} />
            <Tooltip contentStyle={{ direction: 'rtl' }} />
            <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} dot={{ r: compact ? 3 : 4 }} activeDot={{ r: compact ? 4 : 6 }} />
          </LineChart>
        );
      case 'area':
        return (
          <AreaChart data={data} margin={margins}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={compact ? 60 : 80} tick={{ fontSize }} />
            <YAxis tick={{ fontSize }} label={chartSpec.yLabel ? { value: chartSpec.yLabel, angle: -90, position: 'insideLeft', fontSize } : undefined} />
            <Tooltip contentStyle={{ direction: 'rtl' }} />
            <Area type="monotone" dataKey="value" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
          </AreaChart>
        );
      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={compact ? false : ({ name, percent }) => percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : ''}
              outerRadius={compact ? 70 : 100}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ direction: 'rtl' }} formatter={(value, name) => [value, name]} />
            <Legend wrapperStyle={{ direction: 'rtl', fontSize }} />
          </PieChart>
        );
      default:
        return (
          <BarChart data={data} margin={margins}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={compact ? 60 : 80} tick={{ fontSize }} />
            <YAxis tick={{ fontSize }} />
            <Tooltip contentStyle={{ direction: 'rtl' }} />
            <Bar dataKey="value" fill="#8884d8" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
    }
  };

  return (
    <div className={classes.singleChartWrap}>
      {chartSpec.title && <div className={classes.chartTitle}>{chartSpec.title}</div>}
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
      {chartSpec.xLabel && <div className={classes.chartXLabel}>{chartSpec.xLabel}</div>}
    </div>
  );
};

const ResultBox = ({ entry, onAction }) => {
  const { spec, done } = entry;
  const act = (button, rowId) => onAction(entry.result_id, button, rowId);

  return (
    <div className={classes.resultBox}>
      {(spec.title || spec.subtitle) && (
        <div className={classes.resultHead}>
          {spec.title && <div className={classes.resultTitle}>{spec.title}</div>}
          {spec.subtitle && <div className={classes.resultSubtitle}>{spec.subtitle}</div>}
        </div>
      )}

      {spec.kind === 'entity' && (
        <div>
          <div className={classes.entityGrid}>
            {(spec.fields || []).map((f, i) => (
              <div key={i} className={classes.entityField}>
                <div className={classes.fieldLabel}>{f.label}</div>
                <div className={classes.entityValue}>{cellText(f.value)}</div>
              </div>
            ))}
          </div>
          {(spec.actions || []).length > 0 && (
            <div className={classes.resultFoot}>
              <ActionButtons actions={spec.actions} done={done} keyPrefix="ent"
                targetId={spec.entity_id} onAction={(i) => act(i, null)} />
            </div>
          )}
        </div>
      )}

      {spec.kind === 'table' && (() => {
        const rows = spec.rows || [];
        const columns = (spec.columns && spec.columns.length)
          ? spec.columns
          : Object.keys(rows[0] || {}).filter(k => !k.startsWith('_')).map(k => ({ key: k, label: k }));
        return (
          <div className={classes.tableWrap}>
            <table className={classes.resTable}>
              <thead>
                <tr>
                  {columns.map(c => <th key={c.key}>{c.label}</th>)}
                  {(spec.row_actions || []).length > 0 && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    {columns.map(c => <td key={c.key}>{cellText(row[c.key])}</td>)}
                    {(spec.row_actions || []).length > 0 && (
                      <td className={classes.actionCell}>
                        <ActionButtons actions={spec.row_actions} done={done} keyPrefix="row"
                          targetId={row._id} onAction={(i) => act(i, row._id)} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={classes.tableCount}>{rows.length} שורות</div>
          </div>
        );
      })()}

      {spec.kind === 'stats' && (
        <div>
          <div className={classes.statTiles}>
            {(spec.stats || []).map((s, i) => (
              <div key={i} className={classes.statTile}>
                <div className={classes.statValue}>{s.value}</div>
                <div className={classes.statLabel}>{s.label}</div>
                {s.hint && <div className={classes.statHint}>{s.hint}</div>}
              </div>
            ))}
          </div>
          {spec.chart && spec.chart.type === 'bar' && (() => {
            const max = Math.max(...spec.chart.values, 1);
            return (
              <div className={classes.barChart}>
                {spec.chart.labels.map((label, i) => (
                  <div key={i} className={classes.barRow}>
                    <div className={classes.barLabel}>{label}</div>
                    <div className={classes.barTrack}>
                      <div className={classes.barFill} style={{ width: `${(spec.chart.values[i] / max) * 100}%` }} />
                    </div>
                    <div className={classes.barValue}>{spec.chart.values[i]}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {spec.kind === 'chart' && (
        <div className={classes.chartContainer}>
          <SingleChart chartSpec={spec} height={300} />
        </div>
      )}

      {spec.kind === 'charts' && (() => {
        const chartList = spec.charts || [];
        if (chartList.length === 0) {
          return <div className={classes.chartEmpty}>אין גרפים להצגה</div>;
        }
        const count = Math.min(chartList.length, 3); // Max 3 charts
        return (
          <div className={classes.chartsGrid} data-count={count}>
            {chartList.slice(0, 3).map((chartSpec, i) => (
              <div key={i} className={classes.chartsGridItem}>
                <SingleChart chartSpec={chartSpec} height={250} compact={count > 1} />
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
};

// ── Customer card ─────────────────────────────────────────────────────────────

const FIELD_LABELS = [
  ['firstname', 'שם פרטי'], ['lastname', 'שם משפחה'], ['phone', 'טלפון'],
  ['mail', 'מייל'], ['age', 'גיל']
];

const CustomerCard = ({ entry, tags, statuses, onInsert, onDismiss }) => {
  const { card } = entry;
  const isNew = card.kind === 'new';
  const [collapsed, setCollapsed] = useState(false);
  const [fields, setFields] = useState(() => {
    const f = {};
    FIELD_LABELS.forEach(([k]) => { f[k] = card[k] || ''; });
    f.sex = card.sex || '';
    return f;
  });
  const [statusId, setStatusId] = useState(card.status_id || '');
  const [tagIds, setTagIds] = useState(card.tag_ids || []);
  const [history, setHistory] = useState(card.history || '');
  const [updates, setUpdates] = useState(card.updates || {});
  const [working, setWorking] = useState(false);

  const title = [card.firstname, card.lastname].filter(Boolean).join(' ')
    || (card.current && card.current.name) || 'ללא שם';
  const done = entry.status === 'inserted';

  const handleInsert = async () => {
    setWorking(true);
    const edited = { tag_ids: tagIds, history: history.trim() || undefined };
    if (isNew) {
      FIELD_LABELS.forEach(([k]) => { edited[k] = fields[k].trim() || undefined; });
      edited.sex = fields.sex || undefined;
      edited.status_id = statusId ? parseInt(statusId) : undefined;
    } else {
      // Empty inputs are skipped (removed via ✕, not written as empty values)
      edited.updates = {};
      Object.keys(updates).forEach(k => {
        if (String(updates[k]).trim()) edited.updates[k] = String(updates[k]).trim();
      });
    }
    await onInsert(entry.card_id, edited);
    setWorking(false);
  };

  const removeUpdate = (key) => {
    setUpdates(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  return (
    <div className={[classes.custCard, done ? classes.custCardDone : ''].join(' ')}>
      <div className={classes.custHead} onClick={() => setCollapsed(!collapsed)}>
        <span className={[classes.badge, isNew ? classes.badgeNew : classes.badgeExisting].join(' ')}>
          {isNew ? '🆕 לקוח חדש' : '🔄 לקוח קיים'}
        </span>
        <span className={classes.custTitle}>{title}</span>
        {card.phone && <span className={classes.custPhone}>{card.phone}</span>}
        <span className={classes.spacer} />
        <button className={classes.iconBtn} title="הסרה"
          onClick={e => { e.stopPropagation(); onDismiss(entry.card_id); }}>✕</button>
        <span className={classes.iconBtn}>{collapsed ? '◂' : '▾'}</span>
      </div>

      {!collapsed && (
        <div className={classes.custBody}>
          {!isNew && card.current && (
            <div className={classes.custCurrent}>
              📇 במערכת כרגע —
              {card.current.name && ` שם: ${card.current.name} ·`}
              {card.current.phone && ` טלפון: ${card.current.phone} ·`}
              {card.current.mail && ` מייל: ${card.current.mail} ·`}
              {card.current.status_name && ` סטטוס: ${card.current.status_name} ·`}
              {card.current.tag_names && card.current.tag_names.length > 0 && ` תגיות: ${card.current.tag_names.join(', ')}`}
            </div>
          )}

          {isNew ? (
            <div className={classes.custGrid}>
              {FIELD_LABELS.map(([k, label]) => (
                <label key={k}>
                  <div className={classes.fieldLabel}>{label}</div>
                  <input className={classes.input} value={fields[k]}
                    dir={k === 'phone' || k === 'mail' ? 'ltr' : undefined}
                    onChange={e => setFields({ ...fields, [k]: e.target.value })} />
                </label>
              ))}
              <label>
                <div className={classes.fieldLabel}>מין</div>
                <select className={classes.input} value={fields.sex}
                  onChange={e => setFields({ ...fields, sex: e.target.value })}>
                  <option value="">—</option>
                  <option value="female">נקבה</option>
                  <option value="male">זכר</option>
                </select>
              </label>
              <label>
                <div className={classes.fieldLabel}>סטטוס</div>
                <select className={classes.input} value={statusId}
                  onChange={e => setStatusId(e.target.value)}>
                  <option value="">—</option>
                  {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              {/* tags live in the grid next to the status — not a full row */}
              <div>
                <div className={classes.fieldLabel}>תגיות</div>
                <TagPicker tags={tags} selected={tagIds} onChange={setTagIds} />
              </div>
            </div>
          ) : (
            <div className={classes.custGrid}>
              {Object.keys(updates).map(k => (
                <label key={k}>
                  <div className={classes.fieldLabel}>
                    עדכון: {k}
                    <button className={classes.updateRemove} title="ביטול העדכון"
                      onClick={e => { e.preventDefault(); removeUpdate(k); }}>✕</button>
                  </div>
                  <input className={classes.input} dir="ltr" value={updates[k]}
                    onChange={e => setUpdates({ ...updates, [k]: e.target.value })} />
                </label>
              ))}
              <div>
                <div className={classes.fieldLabel}>תגיות להוספה</div>
                <TagPicker tags={tags} selected={tagIds} onChange={setTagIds} />
              </div>
            </div>
          )}

          <div className={classes.fieldLabel}>רשומת היסטוריה</div>
          <textarea className={classes.textarea} value={history}
            onChange={e => setHistory(e.target.value)} />

          <div className={classes.custFoot}>
            <button className={[classes.btn, classes.btnPrimary].join(' ')}
              disabled={done || working} onClick={handleInsert}>
              {working ? '⏳ מבצע...' : isNew ? '➕ הוספה למערכת' : '💾 עדכון + היסטוריה'}
            </button>
            {done && entry.result && (
              <span className={classes.resultOk}>
                ✅ {entry.result.action === 'updated' ? 'עודכן' : 'נוסף'} בהצלחה (לקוח #{entry.result.customer_id})
              </span>
            )}
            {entry.status === 'error' && <span className={classes.resultErr}>❌ {entry.error}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const AiInsert = (props) => {
  const [ctx, setCtx] = useState(null);
  const [feed, setFeed] = useState([]);        // chat feed: user/assistant/note/error/question/result
  const [cards, setCards] = useState([]);       // customer card entries
  const [resultsById, setResultsById] = useState({}); // result components keyed by result_id
  const [busy, setBusy] = useState(false);
  const [partial, setPartial] = useState('');
  const [input, setInput] = useState('');
  const [groupQuery, setGroupQuery] = useState('');
  const [groupOpen, setGroupOpen] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [insertingAll, setInsertingAll] = useState(false);
  const [showDefaults, setShowDefaults] = useState(false);
  const [defStatus, setDefStatus] = useState('');
  const [defTags, setDefTags] = useState([]);
  const [loginUrl, setLoginUrl] = useState(null);
  const [loginCode, setLoginCode] = useState('');
  const [loginWaiting, setLoginWaiting] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const seqRef = useRef(0);
  const pollTimerRef = useRef(null);
  const feedEndRef = useRef(null);
  const composerRef = useRef(null);
  const localEchoRef = useRef([]); // user texts already shown locally — skip their server echo

  const loadContext = async () => {
    try {
      const data = await props.getAiInsertContext();
      setCtx(data);
      if (data.cards) setCards(data.cards);
      if (data.results) {
        setResultsById(prev => {
          const next = { ...prev };
          data.results.forEach(r => { next[r.result_id] = { ...r, ...next[r.result_id] }; });
          return next;
        });
      }
      if (data.defaults) {
        setDefStatus(data.defaults.status_id || '');
        setDefTags(data.defaults.tag_ids || []);
      } else {
        setShowDefaults(true);
      }
    } catch (e) {
      console.error('AI-insert context error:', e);
    }
  };

  const poll = async () => {
    try {
      const data = await props.getAiEvents(seqRef.current);
      setBusy(data.busy);
      setPartial(data.partial || '');
      if (!data.events.length) return;
      seqRef.current = data.seq;
      data.events.forEach(ev => {
        if (ev.type === 'cards') {
          setCards(prev => [...prev, ...ev.cards.filter(c => !prev.some(p => p.card_id === c.card_id))]);
        } else if (ev.type === 'card_result') {
          setCards(prev => prev.map(c => c.card_id === ev.card_id
            ? { ...c, status: ev.ok ? 'inserted' : 'error', result: ev.ok ? ev : c.result, error: ev.error }
            : c));
        } else if (ev.type === 'card_dismissed') {
          setCards(prev => prev.filter(c => c.card_id !== ev.card_id));
        } else if (ev.type === 'defaults_changed') {
          setCtx(prev => prev ? { ...prev, defaults: ev.defaults } : prev);
        } else if (ev.type === 'result') {
          setResultsById(prev => ({ ...prev, [ev.result.result_id]: ev.result }));
          setFeed(prev => [...prev, { type: 'result', result_id: ev.result.result_id }]);
        } else if (ev.type === 'action_result') {
          setResultsById(prev => {
            const entry = prev[ev.result_id];
            if (!entry) return prev;
            return {
              ...prev,
              [ev.result_id]: { ...entry, done: { ...entry.done, [ev.done_key]: ev } }
            };
          });
        } else if (ev.type === 'question_answered') {
          setFeed(prev => prev.map(f => f.type === 'question' && f.id === ev.id ? { ...f, answered: true } : f));
        } else if (ev.type === 'user') {
          const idx = localEchoRef.current.indexOf(ev.text);
          if (idx !== -1) localEchoRef.current.splice(idx, 1); // our own echo — already shown
          else setFeed(prev => [...prev, ev]);
        } else {
          setFeed(prev => [...prev, ev]);
        }
      });
      if (feedEndRef.current) feedEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } catch (e) { /* transient poll errors are fine */ }
  };

  // Poll as a setTimeout chain (not setInterval) so slow responses never
  // overlap — overlapping polls would duplicate feed messages.
  useEffect(() => {
    let stopped = false;
    loadContext();
    const tick = async () => {
      if (stopped) return;
      await poll();
      if (!stopped) pollTimerRef.current = setTimeout(tick, 500);
    };
    tick();
    return () => { stopped = true; clearTimeout(pollTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groups = (ctx && ctx.groups) || [];
  const groupMatches = groups.filter(g =>
    !groupQuery || (g.name || '').toLowerCase().includes(groupQuery.toLowerCase())
  ).slice(0, 10);

  const fetchAndAnalyze = async (group) => {
    setFetching(true);
    setGroupQuery('');
    setFeed(prev => [...prev, { type: 'note', text: `📥 מושך הודעות מ"${group.name}"...` }]);
    try {
      const summary = await props.aiFetchGroup(group.id ? { id: group.id } : { name: group.name });
      setFeed(prev => [...prev, {
        type: 'note',
        text: `📥 נמשכו ${summary.message_count} הודעות — ${summary.participant_count} משתתפים, ${summary.existing_count} כבר קיימים`
      }]);
      await props.aiAnalyzeChat(summary.chat_id);
      setBusy(true);
      loadContext(); // refresh recent chats
    } catch (e) {
      props.showAlert((e.response && e.response.data && e.response.data.error) || 'שגיאה במשיכת הקבוצה');
    }
    setFetching(false);
  };

  const handleFetchClick = () => {
    if (!groupQuery.trim()) return;
    const exact = groups.filter(g => (g.name || '').toLowerCase().includes(groupQuery.toLowerCase()));
    fetchAndAnalyze(exact.length === 1 ? exact[0] : { name: groupQuery.trim() });
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    if (composerRef.current) composerRef.current.style.height = 'auto';
    localEchoRef.current.push(text);
    setFeed(prev => [...prev, { type: 'user', text }]);
    setBusy(true);
    try { await props.aiSendMessage(text); }
    catch (e) {
      props.showAlert((e.response && e.response.data && e.response.data.error) || 'שגיאה בשליחה');
      setBusy(false);
    }
  };

  // Enter sends, Shift+Enter inserts a newline
  const handleComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const autoGrowComposer = (e) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px';
  };

  const handleAnswer = async (id, answers) => {
    setFeed(prev => prev.map(f => f.id === id ? { ...f, answered: true } : f));
    await props.aiAnswer({ id, answers });
  };

  const handleInsertCard = async (cardId, edited) => {
    try {
      const entry = await props.aiInsertCard(cardId, edited);
      setCards(prev => prev.map(c => c.card_id === cardId ? entry : c));
      return entry;
    } catch (e) {
      props.showAlert((e.response && e.response.data && e.response.data.error) || 'שגיאה בהוספה');
      return null;
    }
  };

  // Bulk insert: every pending card, as proposed (per-card edits still apply
  // when inserted individually)
  const handleInsertAll = async () => {
    setInsertingAll(true);
    const pendingIds = cards.filter(c => c.status === 'pending').map(c => c.card_id);
    for (const cardId of pendingIds) {
      // eslint-disable-next-line no-await-in-loop
      await handleInsertCard(cardId);
    }
    setInsertingAll(false);
  };

  // Dismiss also persists the decision (customers.aiMemory) so Claude won't
  // propose this person / this update again
  const handleDismissCard = async (cardId) => {
    setCards(prev => prev.filter(c => c.card_id !== cardId));
    try {
      const res = await props.aiDismissCard(cardId);
      if (res.remembered === 'dismissed') props.showAlert('הוסר — קלוד לא יציע את האיש קשר הזה שוב');
      else if (res.remembered === 'update_dismissed') props.showAlert('הוסר — קלוד לא יציע שוב עדכון ללקוח הזה');
    } catch (e) { /* card already gone from the UI; memory write is best-effort */ }
  };

  // Action button on a result component — direct DB write, no AI involved
  const handleResultAction = async (resultId, button, rowId) => {
    try {
      const outcome = await props.aiResultAction(resultId, button, rowId);
      const isRow = rowId !== null && rowId !== undefined;
      const entry = resultsById[resultId];
      const doneKey = `${isRow ? 'row' : 'ent'}:${button}:${isRow ? rowId : (entry && entry.spec.entity_id)}`;
      setResultsById(prev => {
        const cur = prev[resultId];
        if (!cur) return prev;
        return { ...prev, [resultId]: { ...cur, done: { ...cur.done, [doneKey]: outcome } } };
      });
      if (!outcome.ok) props.showAlert(outcome.error || 'הפעולה נכשלה');
    } catch (e) {
      props.showAlert((e.response && e.response.data && e.response.data.error) || 'הפעולה נכשלה');
    }
  };

  const handleSaveDefaults = async () => {
    const statuses = (ctx && ctx.statuses) || [];
    const tags = (ctx && ctx.tags) || [];
    const status = statuses.find(s => s.id === parseInt(defStatus));
    try {
      const { defaults } = await props.aiSaveDefaults({
        status_id: status ? status.id : null,
        status_name: status ? status.name : null,
        tag_ids: defTags,
        tag_names: defTags.map(id => (tags.find(t => t.id === id) || {}).name).filter(Boolean)
      });
      setCtx(prev => ({ ...prev, defaults }));
      setShowDefaults(false);
      props.showAlert('ברירות המחדל נשמרו');
    } catch (e) {
      props.showAlert('שגיאה בשמירת ברירות מחדל');
    }
  };

  const handleClaudeLogin = async () => {
    try {
      const res = await props.aiClaudeLogin();
      if (res.url) setLoginUrl(res.url);
      else props.showAlert(res.error || 'שגיאה בהתחברות');
    } catch (e) { props.showAlert('שגיאה בהתחברות'); }
  };

  // Code submission is async server-side (nginx timeouts) — poll status until
  // the token exchange finishes
  const handleClaudeCode = async () => {
    try {
      const res = await props.aiClaudeCode(loginCode.trim());
      if (!res.ok) { props.showAlert(res.error || 'הקוד לא התקבל'); return; }
      setLoginWaiting(true);
      const started = Date.now();
      const check = async () => {
        try {
          const s = await props.aiClaudeStatus();
          const phase = (s.login && s.login.phase) || '';
          if (s.status === 'ready' || phase === 'done') {
            setLoginWaiting(false); setLoginUrl(null); setLoginCode('');
            loadContext();
            props.showAlert('החיבור לקלוד הצליח 🎉');
            return;
          }
          if (phase === 'error') {
            setLoginWaiting(false);
            props.showAlert(s.login.error || 'ההתחברות נכשלה');
            console.error('claude login output:', s.login.output);
            return;
          }
        } catch (e) { /* transient */ }
        if (Date.now() - started < 100000) setTimeout(check, 2000);
        else { setLoginWaiting(false); props.showAlert('תם הזמן — נסי שוב'); }
      };
      setTimeout(check, 2000);
    } catch (e) { props.showAlert('שגיאה בשליחת הקוד'); }
  };

  const handleManualToken = async () => {
    try {
      await props.aiClaudeToken(manualToken.trim());
      setManualToken(''); loadContext();
      props.showAlert('הטוקן נשמר');
    } catch (e) {
      props.showAlert((e.response && e.response.data && e.response.data.error) || 'טוקן לא תקין');
    }
  };

  if (!ctx) return <div className={classes.loading}>טוען...</div>;

  const needsLogin = ctx.claude && ctx.claude.status === 'needs_login';
  const tags = ctx.tags || [];
  const statuses = ctx.statuses || [];
  const pendingCount = cards.filter(c => c.status === 'pending').length;

  return (
    <div className={classes.page}>

      {/* ── Actions sidebar ── */}
      <div className={classes.sidebar}>
        <div className={classes.sideCard}>
          <div className={classes.title}>🤖 AI assistant</div>
          <div className={classes.pillRow}>
            <span className={[classes.pill, ctx.whatsapp === 'ready' ? classes.pillOk : classes.pillBad].join(' ')}>
              וואטסאפ {ctx.whatsapp === 'ready' ? 'מחובר' : 'מנותק'}
            </span>
            <span className={[classes.pill, needsLogin ? classes.pillBad : classes.pillOk].join(' ')}>
              קלוד {needsLogin ? 'לא מחובר' : 'מחובר'}
            </span>
          </div>
        </div>

        {/* Claude login */}
        {needsLogin && (
          <div className={classes.sideCard}>
            <div className={classes.cardTitle}>התחברות לקלוד</div>
            {!loginUrl ? (
              <div>
                <button className={[classes.btn, classes.btnPrimary, classes.btnBlock].join(' ')} onClick={handleClaudeLogin}>
                  התחברות עם חשבון Anthropic
                </button>
                <details className={classes.manualToken}>
                  <summary>יש לי טוקן מוכן</summary>
                  <div className={classes.stack}>
                    <input className={classes.input} dir="ltr" placeholder="sk-ant-oat01-..."
                      value={manualToken} onChange={e => setManualToken(e.target.value)} />
                    <button className={[classes.btn, classes.btnDefault].join(' ')} onClick={handleManualToken}>שמירה</button>
                  </div>
                </details>
              </div>
            ) : (
              <div>
                <p>1. פתחי את הקישור, התחברי עם חשבון קלוד, והעתיקי את הקוד:</p>
                <a href={loginUrl} target="_blank" rel="noopener noreferrer" className={classes.loginLink}>{loginUrl}</a>
                <p>2. הדביקי את הקוד כאן:</p>
                <div className={classes.stack}>
                  <input className={classes.input} dir="ltr" value={loginCode}
                    disabled={loginWaiting}
                    onChange={e => setLoginCode(e.target.value)} placeholder="קוד מהדפדפן" />
                  <button className={[classes.btn, classes.btnPrimary].join(' ')}
                    disabled={loginWaiting || !loginCode.trim()} onClick={handleClaudeCode}>
                    {loginWaiting ? '⏳ מתחבר...' : 'אישור'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Group fetch */}
        <div className={classes.sideCard}>
          <div className={classes.cardTitle}>📥 הוספת לקוחות מוואטסאפ</div>
          <div className={classes.stack}>
            <div className={classes.acWrap}>
              <input className={classes.input} placeholder="🔍 שם קבוצה..."
                value={groupQuery}
                onChange={e => { setGroupQuery(e.target.value); setGroupOpen(true); }}
                onFocus={() => setGroupOpen(true)}
                onBlur={() => setTimeout(() => setGroupOpen(false), 150)}
                onKeyDown={e => { if (e.key === 'Enter') handleFetchClick(); }} />
              {groupOpen && groupQuery && groupMatches.length > 0 && (
                <div className={classes.acList}>
                  {groupMatches.map(g => (
                    <div key={g.id} className={classes.acItem}
                      onMouseDown={e => { e.preventDefault(); fetchAndAnalyze(g); }}>
                      {g.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button className={[classes.btn, classes.btnPrimary, classes.btnBlock].join(' ')}
              disabled={fetching || busy || ctx.whatsapp !== 'ready'} onClick={handleFetchClick}>
              {fetching ? '⏳ מושך...' : '📥 משוך ונתח'}
            </button>
          </div>
          {(ctx.recent_chats || []).length > 0 && (
            <div>
              <div className={classes.fieldLabel}>קבוצות אחרונות</div>
              <div className={classes.recentList}>
                {ctx.recent_chats.map(c => (
                  <button key={c.chat_id} className={classes.chip} disabled={fetching || busy}
                    onClick={() => fetchAndAnalyze({ id: c.chat_id, name: c.name })}>
                    📥 {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Defaults */}
        <div className={classes.sideCard}>
          <div className={classes.cardTitle}>ברירות מחדל ללקוחות חדשים</div>
          {(showDefaults || !ctx.defaults) ? (
            <div>
              <div className={classes.fieldLabel}>סטטוס</div>
              <select className={[classes.input, classes.inputBlock].join(' ')} value={defStatus}
                onChange={e => setDefStatus(e.target.value)}>
                <option value="">—</option>
                {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <div className={classes.fieldLabel}>תגיות</div>
              <TagPicker tags={tags} selected={defTags} onChange={setDefTags} />
              <button className={[classes.btn, classes.btnPrimary, classes.btnBlock].join(' ')} onClick={handleSaveDefaults}>שמירה</button>
            </div>
          ) : (
            <div className={classes.defaultsLine}>
              {ctx.defaults.status_name || '—'}
              {ctx.defaults.tag_names && ctx.defaults.tag_names.length > 0 && ` · ${ctx.defaults.tag_names.join(', ')}`}
              <button className={classes.linkBtn} onClick={() => setShowDefaults(true)}>עריכה</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Chat column ── */}
      <div className={classes.chatArea}>
        <div className={classes.feedScroll}>
          {feed.length === 0 && !busy && cards.length === 0 && (
            <div className={classes.emptyFeed}>
              בחרי קבוצת וואטסאפ מסרגל הפעולות, או כתבי לקלוד שאלה על נתוני המערכת 💬
            </div>
          )}

          {feed.map((ev, i) => {
            if (ev.type === 'user') return <div key={i} className={[classes.msg, classes.msgUser].join(' ')}>{ev.text}</div>;
            if (ev.type === 'assistant') return <div key={i} className={[classes.msg, classes.msgAssistant].join(' ')}>{ev.text}</div>;
            if (ev.type === 'note') return <div key={i} className={classes.msgNote}>{ev.text}</div>;
            if (ev.type === 'error') return <div key={i} className={[classes.msg, classes.msgError].join(' ')}>{ev.text}</div>;
            if (ev.type === 'result') {
              const entry = resultsById[ev.result_id];
              return entry ? <ResultBox key={i} entry={entry} onAction={handleResultAction} /> : null;
            }
            if (ev.type === 'question') {
              return (
                <div key={i} className={classes.questionCard}>
                  {(ev.questions || []).map((q, qi) => (
                    <div key={qi}>
                      <div className={classes.qText}>{q.question}</div>
                      {!ev.answered && (
                        <div className={classes.qOptions}>
                          {(q.options || []).map((opt, oi) => (
                            <button key={oi} className={classes.optBtn}
                              onClick={() => handleAnswer(ev.id, { [q.question]: opt.label })}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            }
            return null;
          })}

          {partial && <div className={[classes.msg, classes.msgAssistant].join(' ')}>{partial}</div>}
          {busy && !partial && <div className={classes.msgNote}>⏳ קלוד חושב...</div>}

          {/* Customer cards live inside the scroll area so the composer stays pinned */}
          {cards.length > 0 && (
            <div className={classes.cardsSection}>
              {pendingCount > 1 && (
                <div className={classes.bulkBar}>
                  <button className={[classes.btn, classes.btnPrimary].join(' ')}
                    disabled={insertingAll} onClick={handleInsertAll}>
                    {insertingAll ? '⏳ מוסיף את כולם...' : `➕ הוספת כל ${pendingCount} הכרטיסים הממתינים`}
                  </button>
                </div>
              )}
              {cards.map(entry => (
                <CustomerCard key={entry.card_id} entry={entry} tags={tags} statuses={statuses}
                  onInsert={handleInsertCard} onDismiss={handleDismissCard} />
              ))}
            </div>
          )}

          <div ref={feedEndRef} />
        </div>

        {/* Composer — pinned to the bottom of the chat column */}
        <div className={classes.composer}>
          {busy && (
            <button className={[classes.btn, classes.btnDefault].join(' ')} onClick={() => props.aiInterrupt()}>
              ⏹ עצור
            </button>
          )}
          <textarea ref={composerRef} rows={1}
            className={[classes.input, classes.composerInput].join(' ')}
            placeholder="כתבי הודעה לקלוד... (Shift+Enter לשורה חדשה)"
            value={input}
            disabled={needsLogin}
            onChange={autoGrowComposer}
            onKeyDown={handleComposerKeyDown} />
          <button className={[classes.btn, classes.btnPrimary].join(' ')}
            disabled={needsLogin || !input.trim()} onClick={handleSend}>שליחה</button>
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = dispatch => bindActionCreators({
  getAiInsertContext: actionsCreators.getAiInsertContext,
  aiFetchGroup: actionsCreators.aiFetchGroup,
  aiAnalyzeChat: actionsCreators.aiAnalyzeChat,
  getAiEvents: actionsCreators.getAiEvents,
  aiSendMessage: actionsCreators.aiSendMessage,
  aiAnswer: actionsCreators.aiAnswer,
  aiInterrupt: actionsCreators.aiInterrupt,
  aiInsertCard: actionsCreators.aiInsertCard,
  aiDismissCard: actionsCreators.aiDismissCard,
  aiResultAction: actionsCreators.aiResultAction,
  aiSaveDefaults: actionsCreators.aiSaveDefaults,
  aiClaudeStatus: actionsCreators.aiClaudeStatus,
  aiClaudeLogin: actionsCreators.aiClaudeLogin,
  aiClaudeCode: actionsCreators.aiClaudeCode,
  aiClaudeToken: actionsCreators.aiClaudeToken,
  showAlert: actionsCreators.showAlert,
}, dispatch);

export default connect(null, mapDispatchToProps)(AiInsert);
